/**
 * exportData — the ONE shared data-export helper (global function).
 *
 * Every form that exports a grid/list used to hand-roll its own
 * `new Blob([...], 'text/csv') + createObjectURL + <a download>` (~31 copies).
 * This centralises it: give it the rows + a column mapping and a format, and it
 * builds the file and downloads it. CSV is built inline (RFC-4180 quoting + a
 * BOM so Excel opens UTF-8 cleanly); XLSX uses the already-bundled `xlsx` lib,
 * imported dynamically so it only loads when the user actually exports.
 *
 * Dates/values: format them in the column's `value` accessor via the shared
 * `fmtLocal*` helpers (utils/datetime) — the same way the grid displays them —
 * so the export matches the screen exactly.
 *
 *   await exportRows(rows, [
 *     { header: 'Date', value: r => fmtLocalDateTime(r.createdAt) },
 *     { header: 'User', value: r => r.user },
 *   ], { format: 'csv', filename: 'audit-trail' });
 *
 * For the full ALPACA "Export to" format set (html/rtf/mht/image/docx) use the
 * shared <ExportMenu> dropdown (features/accounting/ExportMenu) — it consumes
 * the same { headers, matrix } shape `toMatrix` produces.
 */

/** One export column: a header + how to pull the (display-formatted) cell. */
export interface ExportColumn<T> {
  header: string;
  value: (row: T) => string | number | null | undefined;
}

export type ExportFormat = 'csv' | 'xlsx';

export interface ExportOptions {
  /** 'csv' (default) or 'xlsx'. */
  format?: ExportFormat;
  /** File name WITHOUT extension — the correct extension is appended. */
  filename: string;
  /** XLSX worksheet name (default 'Sheet1', clamped to Excel's 31-char limit). */
  sheetName?: string;
}

/** Trigger a browser download for a Blob (shared so no page re-implements it). */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  // Revoke after a tick so the click's navigation has surely started.
  setTimeout(() => URL.revokeObjectURL(url), 5_000);
}

/** File-type metadata for the native Save-As picker, keyed by extension. */
const SAVE_TYPES: Record<string, { mime: string; description: string }> = {
  '.csv':  { mime: 'text/csv',                                                                 description: 'CSV File' },
  '.xlsx': { mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',        description: 'XLSX File' },
  '.xls':  { mime: 'application/vnd.ms-excel',                                                 description: 'XLS File' },
  '.pdf':  { mime: 'application/pdf',                                                          description: 'PDF File' },
  '.docx': { mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',  description: 'DOCX File' },
  '.txt':  { mime: 'text/plain',                                                               description: 'Text File' },
  '.xml':  { mime: 'application/xml',                                                          description: 'XML File' },
};

/**
 * Save a generated file, letting the user CHOOSE THE FOLDER — the ALPACA GoLive
 * behaviour (`DownloadController.Download_ExecuteWithSaveAs`): a small
 * "File Ready to Save" confirmation naming the file, then **Save As** opens the
 * native picker so the user can pick any folder, instead of the browser silently
 * dropping the file into Downloads.
 *
 * Mirrors legacy exactly:
 *  • Picker available (Chrome/Edge on HTTPS or localhost) → dialog → Save As →
 *    `showSaveFilePicker` → write. Cancel (either the dialog or the picker)
 *    writes nothing.
 *  • Picker unavailable (Firefox / Safari / plain-HTTP host) → straight to the
 *    normal auto-download, no dialog — same fallback branch legacy has.
 *
 * The dialog is the shared modal (§18.6) — never a hand-rolled overlay.
 *
 * @param filename File name INCLUDING its extension (e.g. "MSRSamples_2026.xlsx").
 * @returns true when the file was written/downloaded, false when the user cancelled.
 */
export async function saveBlobAs(blob: Blob, filename: string): Promise<boolean> {
  // The native folder picker (File System Access API) exists ONLY in a secure
  // context — HTTPS or localhost. On a plain-HTTP host (e.g. http://server:5030)
  // it is undefined, so "Save As" falls back to the browser download; the user
  // still gets a folder prompt if Chrome's "Ask where to save each file" is on.
  const picker = typeof window.showSaveFilePicker === 'function'
    ? window.showSaveFilePicker.bind(window)
    : null;
  const canPick = picker !== null;

  // The confirmation shows in BOTH cases so the flow looks the same everywhere
  // (user request 2026-08-14 — the dialog must work on the deployed host too).
  const { showDialog } = await import('../components/confirm/dialogBus');
  const ok = await showDialog({
    type: 'confirm',
    title: 'File Ready to Save',
    message: filename,
    note: canPick ? undefined
      : 'Your browser will save this to its download folder. To choose a folder, turn on "Ask where to save each file" in the browser settings (a secure HTTPS address enables the folder picker directly).',
    confirmLabel: 'Save As',
    cancelLabel: 'Cancel',
  });
  if (ok !== true) return false;   // user cancelled — nothing written

  if (!picker) {                   // legacy's else-branch: plain download
    downloadBlob(blob, filename);
    return true;
  }

  const dot = filename.lastIndexOf('.');
  const ext = dot >= 0 ? filename.slice(dot).toLowerCase() : '';
  const meta = SAVE_TYPES[ext];
  try {
    const handle = await picker({
      suggestedName: filename,
      ...(meta ? { types: [{ description: meta.description, accept: { [meta.mime]: [ext] } }] } : {}),
    });
    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();
    return true;
  } catch (err) {
    // User dismissed the picker → cancel, exactly like legacy's AbortError branch.
    if (err instanceof DOMException && err.name === 'AbortError') return false;
    // Picker blocked (permission policy / insecure context) → fall back to download.
    downloadBlob(blob, filename);
    return true;
  }
}

/** Build the header row + 2D string matrix from typed rows + columns. Shared so
 *  the same mapping feeds both this helper and the <ExportMenu> format builders. */
export function toMatrix<T>(
  rows: readonly T[],
  columns: readonly ExportColumn<T>[],
): { headers: string[]; matrix: string[][] } {
  const headers = columns.map((c) => c.header);
  const matrix = rows.map((r) =>
    columns.map((c) => {
      const v = c.value(r);
      return v == null ? '' : String(v);
    }),
  );
  return { headers, matrix };
}

/** RFC-4180 CSV cell: wrap in quotes (doubling internal quotes) when the value
 *  contains a comma, quote, or newline. */
function csvCell(s: string): string {
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function ensureExt(name: string, ext: string): string {
  const lower = name.toLowerCase();
  return lower.endsWith(`.${ext}`) ? name : `${name}.${ext}`;
}

/**
 * Export `rows` to CSV or XLSX and download it. The ONE entry point every
 * `onExport` handler should call instead of hand-rolling a Blob.
 */
export async function exportRows<T>(
  rows: readonly T[],
  columns: readonly ExportColumn<T>[],
  opts: ExportOptions,
): Promise<void> {
  const { headers, matrix } = toMatrix(rows, columns);
  const format = opts.format ?? 'csv';

  if (format === 'xlsx') {
    const XLSX = await import('xlsx');
    const ws = XLSX.utils.aoa_to_sheet([headers, ...matrix]);
    const wb = XLSX.utils.book_new();
    // Excel caps sheet names at 31 chars and forbids : \ / ? * [ ].
    const sheet = (opts.sheetName ?? 'Sheet1').replace(/[:\\/?*[\]]/g, ' ').slice(0, 31) || 'Sheet1';
    XLSX.utils.book_append_sheet(wb, ws, sheet);
    XLSX.writeFile(wb, ensureExt(opts.filename, 'xlsx'));
    return;
  }

  // CSV — BOM + CRLF so Excel opens UTF-8 with correct line breaks.
  const lines = [headers.map(csvCell).join(',')];
  for (const cells of matrix) lines.push(cells.map(csvCell).join(','));
  const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, ensureExt(opts.filename, 'csv'));
}
